/* Version: 3.5.155 */
'use client';
import _toConsumableArray from '@babel/runtime/helpers/toConsumableArray';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _merge from 'lodash/merge';
import { useState, useRef } from 'react';
import { isValid } from 'date-fns';
import PropTypes from 'prop-types';
import Dropdown from '../../../components/Dropdown/index.js';
import Errors from '../../../components/Errors/Errors.js';
import Legend from '../../../components/Legend/Legend.js';
import TextInput from '../../../components/TextInput/index.js';
import i18n from '../../../lang/i18n/index.js';
import { hasErrors, handleGroupedInputsBlur, noop } from '../../../utils/functions.js';
import handle from '../../../utils/handle.js';
import safeCustomStyle from '../../../utils/safeCustomStyle.js';
import { MONTH } from './constants.js';
import { FieldSet, RowWrapper, SmallText } from './styledComponents.js';
import { dateIsComplete, parse, getDisabledDays, isBeyondMin, format, isBeyondMax } from './utils.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import { INPUT_VARIANT } from '../../../components/TextInput/constants.js';
import useDerivedState from '../../../hooks/useDerivedState.js';
import useId from '../../../hooks/useId.js';
import useLang from '../../../hooks/useLang.js';
import useTheme from '../../../hooks/useTheme.js';
import { wrapRefObjectAsFunctionalRef } from '../../../components/TextInput/utils.js';
import componentWithName from '../../../prop-types/componentWithName.js';

var legendCustomStyle = {
  legendStyle: {
    padding: '0'
  }
};
var textInputStyle = {
  rootStyle: {
    width: '25%'
  }
};
var dropdownStyle = {
  rootStyle: {
    width: '50%',
    padding: '0 15px'
  }
};
var DateOfBirthExperimental = function DateOfBirthExperimental(_ref) {
  var controlledId = _ref.id,
    _ref$legend = _ref.legend,
    legend = _ref$legend === undefined ? '' : _ref$legend,
    _ref$value = _ref.value,
    value = _ref$value === undefined ? {} : _ref$value,
    _ref$disabledDays = _ref.disabledDays,
    disabledDays = _ref$disabledDays === undefined ? {
      before: new Date(1900, 0, 1),
      after: new Date()
    } : _ref$disabledDays,
    _ref$required = _ref.required,
    required = _ref$required === undefined ? false : _ref$required,
    _ref$requiredDecorato = _ref.requiredDecorator,
    requiredDecorator = _ref$requiredDecorato === undefined ? '' : _ref$requiredDecorato,
    _ref$disabled = _ref.disabled,
    disabled = _ref$disabled === undefined ? false : _ref$disabled,
    _ref$errors = _ref.errors,
    errors = _ref$errors === undefined ? [] : _ref$errors,
    _ref$callouts = _ref.callouts,
    callouts = _ref$callouts === undefined ? false : _ref$callouts,
    _ref$autoFocus = _ref.autoFocus,
    autoFocus = _ref$autoFocus === undefined ? false : _ref$autoFocus,
    _ref$onChange = _ref.onChange,
    onChange = _ref$onChange === undefined ? noop : _ref$onChange,
    _ref$onFocus = _ref.onFocus,
    onFocus = _ref$onFocus === undefined ? noop : _ref$onFocus,
    _ref$variant = _ref.variant,
    variant = _ref$variant === undefined ? INPUT_VARIANT.BASIC : _ref$variant,
    _ref$customStyle = _ref.customStyle,
    customStyle = _ref$customStyle === undefined ? {} : _ref$customStyle,
    _ref$onBlur = _ref.onBlur,
    _onBlur = _ref$onBlur === undefined ? noop : _ref$onBlur,
    _ref$dayInputRef = _ref.dayInputRef,
    dayInputRef = _ref$dayInputRef === undefined ? null : _ref$dayInputRef,
    _ref$monthInputRef = _ref.monthInputRef,
    monthInputRef = _ref$monthInputRef === undefined ? null : _ref$monthInputRef,
    _ref$yearInputRef = _ref.yearInputRef,
    yearInputRef = _ref$yearInputRef === undefined ? null : _ref$yearInputRef,
    invalidDateLabel = _ref.invalidDateLabel,
    beyondMinLabel = _ref.beyondMinLabel,
    beyondMaxLabel = _ref.beyondMaxLabel,
    tooltip = _ref.tooltip;
  var _useDerivedState = useDerivedState(useId('dob-'), controlledId),
    _useDerivedState2 = _slicedToArray(_useDerivedState, 1),
    id = _useDerivedState2[0];
  var _safeCustomStyle = safeCustomStyle(customStyle),
    fieldSetStyle = _safeCustomStyle.fieldSetStyle,
    rowWrapperStyle = _safeCustomStyle.rowWrapperStyle;
  var yearInputStyle = customStyle.yearInputStyle,
    monthInputStyle = customStyle.monthInputStyle,
    dayInputStyle = customStyle.dayInputStyle;
  var lang = useLang();
  var theme = useTheme();
  var _useState = useState(),
    _useState2 = _slicedToArray(_useState, 2),
    error = _useState2[0],
    setError = _useState2[1];
  var yearValue = value.year,
    month = value.month,
    dayValue = value.day;
  var internalDayInputRef = useRef(null);
  var internalMonthInputRef = useRef(null);
  var internalYearInputRef = useRef(null);
  var dayRef = dayInputRef || internalDayInputRef;
  var monthRef = monthInputRef || internalMonthInputRef;
  var yearRef = yearInputRef || internalYearInputRef;
  var groupedRefs = [dayRef, monthRef, yearRef];

  // Convert day and year to string values if numbers are passed
  var day = dayValue ? "".concat(dayValue) : '';
  var year = yearValue ? "".concat(yearValue) : '';
  function dateIsInvalid(newYear, newMonth, newDay) {
    var date = parse(newYear, newMonth, newDay);
    if (!isValid(date)) {
      return new Error(invalidDateLabel || i18n[lang].dateOfBirth.invalidDate);
    }
    var _getDisabledDays = getDisabledDays(disabledDays),
      before = _getDisabledDays.before,
      after = _getDisabledDays.after;
    if (isBeyondMin(date, before)) {
      return new Error(beyondMinLabel || i18n[lang].dateOfBirth.beyondMinTemplate({
        before: format(before, lang)
      }));
    }
    if (isBeyondMax(date, after)) {
      return new Error(beyondMaxLabel || i18n[lang].dateOfBirth.beyondMaxTemplate({
        after: format(after, lang)
      }));
    }
    return null;
  }
  function handleValidation(newYear, newMonth, newDay) {
    if (dateIsComplete(newYear, newMonth, newDay)) {
      setError(dateIsInvalid(newYear, newMonth, newDay));
    } else {
      setError();
    }
  }
  function handleDateChange(newYear, newMonth, newDay, event) {
    setError();
    var date;
    if (dateIsComplete(newYear, newMonth, newDay) && !dateIsInvalid(newYear, newMonth, newDay)) {
      date = parse(newYear, newMonth, newDay);
    }
    handle(onChange)(event, {
      year: newYear,
      month: newMonth,
      day: newDay,
      date: date
    });
  }
  function renderFormattedLabel(label, placeholder) {
    return /*#__PURE__*/jsxs("span", {
      children: [label, "\xA0", placeholder &&
      /*#__PURE__*/
      // eslint-disable-next-line react/jsx-one-expression-per-line
      jsxs(SmallText, {
        theme: theme,
        children: ["(", placeholder.toLowerCase(), ")"]
      })]
    });
  }
  var errorList = [{
    isError: !!error,
    msg: error && error.message ? error.message : null,
    id: 'dob-internal-error'
  }].concat(_toConsumableArray(errors));
  var hasError = hasErrors(errorList);
  var ariaDescribedBy = hasError ? "".concat(id, "-errors") : undefined;
  return /*#__PURE__*/jsxs(FieldSet, {
    "aria-describedby": ariaDescribedBy,
    fieldSetStyle: fieldSetStyle,
    children: [/*#__PURE__*/jsxs(Legend, {
      customStyle: legendCustomStyle,
      tooltip: tooltip,
      children: [legend || i18n[lang].dateOfBirth.legend, "\xA0", requiredDecorator]
    }), /*#__PURE__*/jsxs(RowWrapper, {
      rowWrapperStyle: rowWrapperStyle,
      children: [/*#__PURE__*/jsx(TextInput, {
        id: "".concat(id, "-day"),
        label: renderFormattedLabel(i18n[lang].dateOfBirth.day, i18n[lang].dateOfBirth.dayPlaceholder),
        value: day,
        onChange: function onChange(newDay, event) {
          return handleDateChange(year, month, newDay, event);
        },
        onBlur: function onBlur(newDay, event) {
          handleGroupedInputsBlur(event, _onBlur, groupedRefs);
          handleValidation(year, month, newDay);
        },
        onFocus: onFocus,
        placeholder: i18n[lang].dateOfBirth.dayPlaceholder,
        inputMode: "numeric",
        autoComplete: "bday-day",
        mask: "99",
        variant: variant,
        autoFocus: autoFocus,
        required: required,
        disabled: disabled,
        customStyle: _merge({}, textInputStyle, yearInputStyle),
        outline: hasError,
        "aria-describedby": ariaDescribedBy,
        ref: wrapRefObjectAsFunctionalRef(dayRef)
      }), /*#__PURE__*/jsx(Dropdown, {
        id: "".concat(id, "-month"),
        label: i18n[lang].dateOfBirth.month,
        placeholder: i18n[lang].dateOfBirth.monthPlaceholder,
        value: month,
        onChange: function onChange(newMonth, event) {
          handleDateChange(year, newMonth, day, event);
          handleValidation(year, newMonth, day);
        },
        onBlur: function onBlur(event) {
          return handleGroupedInputsBlur(event, _onBlur, groupedRefs);
        },
        dropdownItems: Object.keys(MONTH).map(function (monthName) {
          return {
            value: MONTH[monthName],
            label: i18n[lang].global.month[MONTH[monthName]]
          };
        }),
        customStyle: _merge({}, dropdownStyle, monthInputStyle),
        variant: variant,
        required: required,
        autoComplete: "bday-month",
        disabled: disabled,
        outline: hasError,
        "aria-describedby": ariaDescribedBy,
        ref: monthRef
      }), /*#__PURE__*/jsx(TextInput, {
        id: "".concat(id, "-year"),
        label: renderFormattedLabel(i18n[lang].dateOfBirth.year, i18n[lang].dateOfBirth.yearPlaceholder),
        value: year,
        onChange: function onChange(newYear, event) {
          return handleDateChange(newYear, month, day, event);
        },
        onBlur: function onBlur(newYear, event) {
          handleGroupedInputsBlur(event, _onBlur, groupedRefs);
          handleValidation(newYear, month, day);
        },
        onFocus: onFocus,
        placeholder: i18n[lang].dateOfBirth.yearPlaceholder,
        inputMode: "numeric",
        autoComplete: "bday-year",
        mask: "9999",
        variant: variant,
        required: required,
        disabled: disabled,
        customStyle: _merge({}, textInputStyle, dayInputStyle),
        outline: hasError,
        "aria-describedby": ariaDescribedBy,
        ref: wrapRefObjectAsFunctionalRef(yearRef)
      })]
    }), /*#__PURE__*/jsx(Errors, {
      id: id,
      callouts: callouts,
      errors: errorList
    })]
  });
};
process.env.NODE_ENV !== "production" ? DateOfBirthExperimental.propTypes = {
  /** DOM element id */
  id: PropTypes.string,
  /** Label to describe the group of inputs */
  legend: PropTypes.node,
  /** the values of the individual fields */
  value: PropTypes.shape({
    year: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    month: PropTypes.oneOf([MONTH.JANUARY, MONTH.FEBRUARY, MONTH.MARCH, MONTH.APRIL, MONTH.MAY, MONTH.JUNE, MONTH.JULY, MONTH.AUGUST, MONTH.SEPTEMBER, MONTH.OCTOBER, MONTH.NOVEMBER, MONTH.DECEMBER]),
    day: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  }),
  /**
   * date range that should represent disabled dates
   * before defaults to Jan 1, 1900
   * after defaults to today's date
   * */
  disabledDays: PropTypes.shape({
    /** dates before this date are disabled */
    before: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    /** dates after this date are disabled */
    after: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)])
  }),
  /** HTML 5 required attribute */
  required: PropTypes.bool,
  /** text that is appended to label for elements marked as required */
  requiredDecorator: PropTypes.string,
  /** HTML 5 disabled attribute */
  disabled: PropTypes.bool,
  /** An array of error messages to be displayed. */
  errors: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    msg: PropTypes.any,
    isError: PropTypes.bool
  }))]),
  /** Sets `aria-atomic=true`, `aria-live=polite` and `role=alert` on error messages (ensures errors are announced by screen readers as soon as they happen). */
  callouts: PropTypes.bool,
  /** HTML 5 autofocus attribute */
  autoFocus: PropTypes.bool,
  /** Which style of input to use */
  variant: PropTypes.oneOf([INPUT_VARIANT.BASIC, INPUT_VARIANT.BASIC_LINE]),
  /** onchange handler for input */
  onChange: PropTypes.func,
  /** onfocus handler for input */
  onFocus: PropTypes.func,
  /** onblur handler for input
   *
   * Called only when focus leaves the component entirely.
   * It is not called when moving focus internally between the day, month, and year fields.
   */
  onBlur: PropTypes.func,
  /** Style overrides */
  customStyle: PropTypes.shape({
    fieldSetStyle: PropTypes.object,
    rowWrapperStyle: PropTypes.object,
    yearInputStyle: PropTypes.object,
    monthInputStyle: PropTypes.object,
    dayInputStyle: PropTypes.object
  }),
  /** ref for the day input element */
  dayInputRef: PropTypes.shape({
    current: PropTypes.elementType
  }),
  /** ref for the month input element */
  monthInputRef: PropTypes.shape({
    current: PropTypes.elementType
  }),
  /** ref for the year input element */
  yearInputRef: PropTypes.shape({
    current: PropTypes.elementType
  }),
  /** Override for error label for an invalid date */
  invalidDateLabel: PropTypes.string,
  /** Override for error when disabledDays.before is provided and date is before disabledDays.before */
  beyondMinLabel: PropTypes.string,
  /** Override for error when disabledDays.after is provided and date is after disabledDays.after */
  beyondMaxLabel: PropTypes.string,
  /** [Tooltip](/docs/tooltip) displayed beside the label */
  tooltip: componentWithName('Tooltip')
} : undefined;

export { DateOfBirthExperimental as default };
